'use client';

import '@statura/ui/src/styles/base.css';

import { deferNavBadgeRead } from '@/lib/navigation/defer-nav-badges';

/**
 * DashboardShellV2 — Phase 1 scaffold for Nav v2.
 *
 * Renders the empty 7-domain shell (aside + topbar + {children}) when the
 * Nav v2 feature flag is on. The aside is a placeholder — domain tiles, pins,
 * and Now cards land in Phase 2 + Phase 4. The topbar wires facility
 * switcher, notification bell, and the user menu so day-to-day chrome is
 * available immediately.
 *
 * Critical invariant: every gate the legacy `DashboardShell` enforces MUST
 * fire here too — flipping the flag must NEVER bypass auth, customer-legal,
 * subscription-block, module-entitlement, or role-cohort checks. See spec
 * §0 hard constraints.
 *
 * path-to-go S22: legacy `dashboard-shell.tsx` deleted. This file is the sole
 * dashboard shell. packages/ui `DashboardLayout` has zero app runtime callers
 * after the deletion (package unit tests remain; app-layer retirement complete).
 */

import {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Menu } from 'lucide-react';
import {
  Card,
  CardContent,
  DashboardPageHeader,
  StateKit,
  PageContainer,
  SectionTabsProvider,
  controlSizeClassName,
} from '@statura/ui';
import {
  PROVIDER_RUNTIME_EVIDENCE_REVISION,
  providerRuntimeEvidenceEnabledForSupabaseUrl,
} from '@statura/experience-contract';
import { AppLoadingState } from './app-loading-state';
import { readShellConfig } from '@/lib/shell/read-shell-config';
import { setActiveProgramLensAction } from '@/lib/experience/program-lens-actions';
import {
  addressBarProgramAction,
  decideRememberAttempt,
  programLensToRemember,
} from '@/lib/experience/remember-url-program-lens';
import { AuthGuard } from './auth-guard';
import { SessionSecurityProvider } from './session-security-provider';
import { FacilitySwitcher } from './facility-switcher';
import { NotificationBell } from './notification-bell';
import { DeferredCommandPalette } from './deferred-command-palette';
import type { AuthUserProfile } from '../stores/auth-store';
import { fetchTenantConfig, fetchSubscriptionBanner } from '../lib/supabase/tenant-shell-queries';
import { SubscriptionBanner } from './subscription-banner';
import { MfaEnforcementBanner } from './mfa-enforcement-banner';
import { TenantSetupBanner } from './onboarding-banner';
import { DemoEnvironmentBanner } from './demo-environment-banner';
import { SystemAnnouncementBanner } from './system-announcement-banner';
import { HelpChatWidget } from './help-chat-widget';
import { PageHelpOpener, dispatchOpenHelpChat } from './help/page-help-opener';
import { KeyboardShortcutsHint } from './keyboard-shortcuts-hint';
import { formatUserRole } from '@/lib/format';
import { ModuleProvider } from '../contexts/module-context';
import { TenantTimezoneProvider } from '../contexts/tenant-timezone-context';
import {
  OrgLabelsProvider,
  resolveOrgLabels,
  setCachedOrgType,
} from '../contexts/org-labels-context';
import { resolveOrgLabelsForProgramLens } from '@/lib/org-labels';
import { ModuleGate } from './module-gate';
import { RoleGate } from './role-gate';
import { CustomerLegalGate } from './customer-legal-gate';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { NavV2Sidebar, navV2ItemIsVisible } from './nav-v2/sidebar/sidebar';
import { ResponsiveSidebar } from './nav-v2/sidebar/responsive-sidebar';
import { NavV2SidebarCollapsed } from './nav-v2/sidebar/sidebar-collapsed';
import { NavV2SectionTabs } from './nav-v2/section-tabs';
import { NavV2MobileDrawer } from './nav-v2/sidebar/mobile-drawer';
import { TopbarUserMenu } from './nav-v2/topbar/user-menu';
import {
  DEFAULT_PINS_BY_ROLE,
  FOOTER_NAV_V2,
  NAV_V2,
  defaultPinsForRoleAndProvider,
  type NavBadgeKey,
  type ProviderType,
} from '@/lib/navigation/nav-v2';
import {
  loadNavV2BadgeCounts,
  PEOPLE_BADGE_INVALIDATED_EVENT,
  isPeopleBadgeStorageKey,
  noteExternalPeopleBadgeInvalidation,
  selectNavBadgeCountsForTenant,
  type TenantKeyedNavBadgeState,
} from '@/lib/navigation/nav-v2-badges';
import { defaultSidebarPinsForBillingPlan } from '@/lib/navigation/sidebar-default-pins';
import { primaryAliasForLiveRole } from '@/lib/navigation/role-aliases';
import { isRoleAllowedForPath } from '@/lib/route-access';
import { fetchCustomerLegalStatusWithRetry } from '../lib/customer-legal-client';
import {
  isComplianceOnlyPlan,
  isHrWorkerOnlyGoLiveScope,
  type SubscriptionStatus,
} from '../lib/billing';
import { deriveShellModuleState, type InitialShellContext } from '../lib/shell/derive-shell-state';
import type { CustomerLegalState } from '@/lib/customer-legal-shared';
import { trackNavV2Event, type NavV2EventSource } from '@/lib/nav-v2/telemetry';
import { suppressOperationalShellBanners } from '@/lib/shell-banner-policy';
import { DEFAULT_TIMEZONE } from '@/lib/timezone';
import { isComplianceTrialModuleSet } from '@/lib/compliance-trial-access';
import { useGovernmentIntegrations } from '@/contexts/government-integration-context';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import { QueryAuthBoundaryContext } from '../../providers/query-provider';
import { admitServerShellSeed } from '@/lib/auth/server-seed-admission';
import {
  trustedProgramMarkerFamily,
  constrainProviderProgramContextToTenant,
  type ProviderProgramContext,
} from '@/lib/navigation/provider-type';

/** Sidebar badge keys resolved by loadNavV2BadgeCounts. */
const S8_NAV_BADGE_KEYS: readonly NavBadgeKey[] = [
  'sirs_open_24h',
  'mar_unsigned_today',
  'tasks_due_today',
  'people_cannot_work',
];

/**
 * Open the existing global CommandPalette by dispatching a synthetic
 * ⌘K keydown — matches the listener at command-palette.tsx:843-852.
 * Phase 3 will replace this with an imperative handle when the palette
 * is rebuilt; until then the synthetic event keeps the wiring decoupled
 * (no modification of command-palette.tsx required).
 *
 * Phase 6 telemetry: fires `nav_v2_command_palette_opened` with the
 * triggering surface so we can see open-frequency per surface in Sentry
 * breadcrumbs. `keyboard-shortcuts-hint.tsx`'s native ⌘K handler in the
 * palette itself emits `source='shortcut'` directly to keep this
 * function the single source of truth for button-triggered opens.
 */
function triggerCmdK(source: NavV2EventSource = 'sidebar') {
  if (typeof document === 'undefined') return;
  if (document.querySelector('[data-command-palette-root="true"]')) return;
  trackNavV2Event('nav_v2_command_palette_opened', { source });
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
}

function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function DashboardShellV2Inner({
  children,
  initialOrgType,
  initialProviderType,
  initialProviderProgramContext,
  experienceBrandEnabled,
  initialShellContext,
  initialAuthUser,
  initialProgramLensRemembered,
  initialAssistantConversationEnabled,
  initialAssistantProactiveEnabled,
}: {
  children: React.ReactNode;
  initialOrgType?: string | null;
  initialProviderType?: ProviderType;
  initialProviderProgramContext?: ProviderProgramContext;
  experienceBrandEnabled?: boolean;
  initialShellContext?: InitialShellContext;
  initialAuthUser?: AuthUserProfile | null;
  initialAssistantConversationEnabled?: boolean;
  initialAssistantProactiveEnabled?: boolean;
  /** Server fact: the lens cookie already holds the programme this render resolved. */
  initialProgramLensRemembered?: boolean;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const user = useResolvedAuthUser();
  const authBoundary = useContext(QueryAuthBoundaryContext);
  const lastCommittedIdentity = authBoundary?.getLastCommittedIdentity() ?? null;
  const admittedShellContext = useMemo(
    () => admitServerShellSeed(initialShellContext, initialAuthUser, user, lastCommittedIdentity),
    [initialShellContext, initialAuthUser, user, lastCommittedIdentity],
  );
  const admittedOrgType = admittedShellContext ? initialOrgType : undefined;
  const admittedProviderType = admittedShellContext ? initialProviderType : undefined;
  const admittedProviderProgramContext = admittedShellContext
    ? initialProviderProgramContext
    : undefined;

  useEffect(() => {
    if (experienceBrandEnabled) document.documentElement.dataset.experienceBrand = 'true';
    else delete document.documentElement.dataset.experienceBrand;
  }, [experienceBrandEnabled]);

  // Shell a11y (Tier-2, WCAG 2.4.3): the Next App Router does not move focus on
  // client-side navigation, so screen-reader users aren't told the page changed
  // and keyboard focus is stranded on the (often unmounted) nav item. On each
  // route change after the first render, move focus to the #main-content landmark
  // — UNLESS the newly-rendered page already focused something inside main (e.g.
  // an intentional autofocus), which we leave untouched.
  const isFirstRouteRender = useRef(true);
  useEffect(() => {
    if (isFirstRouteRender.current) {
      isFirstRouteRender.current = false;
      return;
    }
    const main = document.getElementById('main-content');
    if (!main) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== main && main.contains(active)) return;
    // Defer past insertion/layout effects so focus moves don't schedule updates
    // during the commit phase that triggered this route change.
    queueMicrotask(() => main.focus({ preventScroll: true }));
  }, [pathname]);

  useEffect(() => {
    const id = window.location.hash.replace(/^#/, '').trim();
    if (!id) return undefined;
    const target = document.getElementById(id);
    const main = document.getElementById('main-content');
    if (!target || !main || !main.contains(target)) return undefined;
    const frame = window.requestAnimationFrame(() => {
      target.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pathname, searchParams]);
  const tenantId = user?.tenantId;
  const isRosterDispatchFullWorkspace =
    pathname === '/roster/dispatch' && searchParams.get('workspace') === 'full';

  // ── Module + tenant-config state (mirror of v1 — see file header note) ──
  // Loading-UX phase 2 — seed gate-critical state from the server-resolved
  // context so the shell renders the real workspace on first paint. Undefined
  // → fall back to client-side resolution (modulesLoaded starts false → loader).
  // Preserve a server-resolved `null` verbatim — null means "all modules
  // enabled" (demo / enterprise tenants) and ModuleGate bypasses gating on it.
  // A `?? []` here would collapse that into an explicit empty array (= NO
  // modules), making ModuleGate render "Module Not Available" on first paint
  // until the client refresh-effect re-derives null — the navigation flicker.
  // Only fall back to [] when there is no seeded context at all, in which case
  // modulesLoaded is false and ModuleGate shows the loader, not the blocked card.
  const [enabledModules, setEnabledModules] = useState<string[] | null>(
    admittedShellContext ? admittedShellContext.enabledModules : [],
  );
  const isComplianceTrial = isComplianceTrialModuleSet(enabledModules);
  const governmentIntegrations = useGovernmentIntegrations();
  const [modulesLoaded, setModulesLoaded] = useState(Boolean(admittedShellContext));
  const [isCiFree, setIsCiFree] = useState(admittedShellContext?.isCiFree ?? false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [moduleLoadError, setModuleLoadError] = useState(
    admittedShellContext?.moduleLoadError ?? false,
  );
  const [isSubBlocked, setIsSubBlocked] = useState(admittedShellContext?.isSubBlocked ?? false);
  const [isDemoTenant, setIsDemoTenant] = useState(admittedShellContext?.isDemoTenant ?? false);
  const [visibleGoLiveScope, setVisibleGoLiveScope] = useState(
    admittedShellContext?.visibleGoLiveScope ?? null,
  );
  const [tenantHiddenNavigationPaths, setTenantHiddenNavigationPaths] = useState<string[]>(
    admittedShellContext?.tenantHiddenNavigationPaths ?? [],
  );
  const [frontlineSelfServiceOnly, setFrontlineSelfServiceOnly] = useState(
    admittedShellContext?.frontlineSelfServiceOnly ?? false,
  );
  // Read by client navigation/components, but resolved by the server shell.
  // Missing context fails closed, matching isShiftSwapsEnabledForTenant.
  const shiftSwapsEnabled = admittedShellContext?.shiftSwapsEnabled ?? false;
  // Seed from the SERVER-resolved orgType (prop) — identical SSR + client first render, so no
  // residential flash and no hydration mismatch (was a localStorage read during render). The
  // tenant-config effect below stays authoritative for live changes.
  const [orgLabels, setOrgLabels] = useState(() =>
    resolveOrgLabels(admittedOrgType ?? undefined, false),
  );
  const [onboardingStepsCompleted, setOnboardingStepsCompleted] = useState<number | undefined>();
  const [onboardingStepsTotal, setOnboardingStepsTotal] = useState<number | undefined>();
  const [onboardingStepsDeferred, setOnboardingStepsDeferred] = useState(0);
  const [onboardingStepsBlocked, setOnboardingStepsBlocked] = useState(0);
  const [bannerData, setBannerData] = useState<{
    subscriptionStatus: SubscriptionStatus;
    trialEndsAt: string | null;
    declaredBeds: number;
    declaredClients: number;
    actualBeds: number | null;
    actualClients: number | null;
  } | null>(null);
  const [customerLegalState, setCustomerLegalState] = useState<CustomerLegalState | null>(
    admittedShellContext?.customerLegalState ?? null,
  );
  const [customerLegalLoaded, setCustomerLegalLoaded] = useState(
    admittedShellContext?.customerLegalLoaded ?? false,
  );
  const [customerLegalError, setCustomerLegalError] = useState<string | null>(null);
  const [shellConfigError, setShellConfigError] = useState(false);
  const [shellConfigAttempt, setShellConfigAttempt] = useState(0);
  const resolvedShellTenantId = useRef(tenantId);
  // Skip the initial client refresh when the server already confirmed the
  // customer-legal state — otherwise the mount refresh resets customerLegalLoaded
  // to false and re-triggers the full-screen gate.
  const skipInitialLegalRefreshRef = useRef(admittedShellContext?.customerLegalLoaded === true);

  async function refreshCustomerLegalState() {
    if (!user || user.role === 'CLIENT' || user.role === 'FAMILY_CARER') {
      setCustomerLegalState(null);
      setCustomerLegalError(null);
      setCustomerLegalLoaded(true);
      return;
    }

    setCustomerLegalLoaded(false);
    setCustomerLegalError(null);
    try {
      setCustomerLegalState(await fetchCustomerLegalStatusWithRetry());
    } catch (error) {
      setCustomerLegalError(
        error instanceof Error ? error.message : 'Unable to load customer agreement status.',
      );
    } finally {
      setCustomerLegalLoaded(true);
    }
  }

  useEffect(() => {
    if (!tenantId) return;

    // Do NOT pre-emptively clear isDemoTenant here — see dashboard-shell.tsx.
    // With server-seeded modulesLoaded=true a transient reset would briefly drop
    // demo status until the fetch resolves. deriveShellModuleState sets the
    // definitive value below; the error callback fails closed to false.
    setShellConfigError(false);
    const cancelConfigRead = readShellConfig({
      read: () => fetchTenantConfig(tenantId),
      onSuccess: (config) => {
        // Same pure derivation the server used to seed initialShellContext —
        // keeps the client refresh byte-identical to the server-resolved gate
        // state (no flash, no entitlement drift). See lib/shell/derive-shell-state.
        const brandConfig = (config as Record<string, unknown> | null)?.brandConfig as
          | Record<string, unknown>
          | null
          | undefined;
        const goLiveScope = brandConfig?.visibleGoLiveScope;
        const hideOnboardingWizard = isHrWorkerOnlyGoLiveScope(goLiveScope);
        void { goLiveScope };
        const derived = deriveShellModuleState(config as Record<string, unknown> | null);

        setEnabledModules(derived.enabledModules);
        setModuleLoadError(derived.moduleLoadError);
        setIsDemoTenant(derived.isDemoTenant);
        setDemoPlanModules(derived.demoPlanModules);
        setIsCiFree(derived.isCiFree);
        setIsSubBlocked(derived.isSubBlocked);
        setVisibleGoLiveScope(derived.visibleGoLiveScope);
        setTenantHiddenNavigationPaths(derived.tenantHiddenNavigationPaths);
        setFrontlineSelfServiceOnly(derived.frontlineSelfServiceOnly);

        if (derived.orgLabelsMode === 'set') {
          setOrgLabels(resolveOrgLabels(derived.orgType, true));
          setCachedOrgType(derived.orgType);
        } else {
          setOrgLabels((current) => ({ ...current, isLoaded: true }));
        }

        // Onboarding banner (non-gating). Only the normal path produces it.
        if (derived.onboarding) {
          const ob = derived.onboarding;
          if (ob.hideWizard || hideOnboardingWizard) {
            setShowOnboarding(false);
            setOnboardingStepsCompleted(undefined);
            setOnboardingStepsTotal(undefined);
            setOnboardingStepsDeferred(0);
            setOnboardingStepsBlocked(0);
          } else if (!ob.onboardingCompleted) {
            if (ob.hasCurrentStep) {
              setShowOnboarding(true);
              setOnboardingStepsCompleted(ob.summary.resolvedSteps);
              setOnboardingStepsTotal(ob.summary.totalSteps);
              setOnboardingStepsDeferred(ob.summary.deferredSteps);
              setOnboardingStepsBlocked(ob.summary.blockedSteps);
            } else if (!ob.hasPlanOrTier) {
              import('../lib/supabase/client').then(({ createClient }) => {
                const sb = createClient();
                Promise.all([
                  sb
                    .from('facilities')
                    .select('id', { count: 'exact', head: true })
                    .eq('tenant_id', tenantId),
                  sb
                    .from('clients')
                    .select('id', { count: 'exact', head: true })
                    .eq('tenant_id', tenantId),
                ])
                  .then(([facResult, clientResult]) => {
                    const hasFacilities = (facResult.count ?? 0) > 0;
                    const hasClients = (clientResult.count ?? 0) > 0;
                    if (!hasFacilities && !hasClients) setShowOnboarding(true);
                  })
                  .catch(() => {
                    /* fail silently */
                  });
              });
            }
          }
        }

        resolvedShellTenantId.current = tenantId;
        setModulesLoaded(true);
        setShellConfigError(derived.moduleLoadError);
      },
      onError: () => {
        setShellConfigError(true);
        setOrgLabels((current) => ({ ...current, isLoaded: true }));
        setEnabledModules([]);
        setModulesLoaded(true);
        setModuleLoadError(true);
        setIsDemoTenant(false);
        setDemoPlanModules(null);
        setVisibleGoLiveScope(null);
        setTenantHiddenNavigationPaths([]);
        setFrontlineSelfServiceOnly(false);
      },
    });

    fetchSubscriptionBanner(tenantId)
      .then((data) => {
        if (data) setBannerData(data);
      })
      .catch(() => {
        /* fail silently — banner just won't show */
      });
    return cancelConfigRead;
  }, [tenantId, shellConfigAttempt]);

  useEffect(() => {
    if (skipInitialLegalRefreshRef.current) {
      // Server already confirmed the customer-legal state for the initial
      // tenant/role — don't reset customerLegalLoaded on mount.
      skipInitialLegalRefreshRef.current = false;
      return;
    }
    void refreshCustomerLegalState();
  }, [user?.role, tenantId]);

  // Client-side fallback: redirect CLIENT/FAMILY_CARER who got past middleware
  useEffect(() => {
    if (user?.role === 'CLIENT' || user?.role === 'FAMILY_CARER') {
      let portalUrl = process.env.NEXT_PUBLIC_CLIENT_PORTAL_URL || 'http://localhost:3001';
      if (!portalUrl.startsWith('http')) portalUrl = `https://${portalUrl}`;
      window.location.href = portalUrl;
    }
  }, [user?.role]);

  const displayName = user
    ? `${user.firstName} ${user.lastName}`.trim() || user.email
    : 'Admin User';

  const initials = user
    ? `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase() || 'AU'
    : 'AU';

  const roleName = user?.role ? formatUserRole(user.role) : 'Coordinator';
  const openHelpChat = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('open-help-chat', {
        detail: { path: pathname },
      }),
    );
  }, [pathname]);

  // ── Nav v2 user state (Phase 2) ─────────────────────────────────────
  // Pin order, collapsed state. Both persisted via layout_preferences /
  // useUserPreferences — same auth-UUID-keyed write path the flag
  // resolver reads in (dashboard)/layout.tsx.
  //
  // CRITICAL — Rules of Hooks: these hooks MUST run on every render, in
  // the same order. The loading early-return below cannot precede them.
  // Phase 2 audit P0 (Paul's review of PR #152): the original Phase 2
  // commit placed these hooks AFTER the early return — first render
  // (modulesLoaded=false) returned early skipping them; second render
  // (modulesLoaded=true) called them, changing hook count between
  // renders. React crashes with "Rendered more hooks than during the
  // previous render." Fix: hooks first, conditional render after.
  const providerType = admittedProviderType ?? 'UNKNOWN';
  const receivedProviderProgramContext = useMemo<ProviderProgramContext>(() => {
    if (admittedProviderProgramContext) return admittedProviderProgramContext;
    if (admittedProviderType && admittedProviderType !== 'UNKNOWN') {
      return {
        tenantId: tenantId ?? null,
        providerType: admittedProviderType,
        resolutionStatus:
          admittedProviderType === 'MIXED' ? ('UNKNOWN' as const) : ('RESOLVED_SINGLE' as const),
        activeProgramFamily:
          admittedProviderType === 'SAH' || admittedProviderType === 'RAC'
            ? admittedProviderType
            : null,
        eligibleProgramFamilies:
          admittedProviderType === 'SAH' || admittedProviderType === 'RAC'
            ? [admittedProviderType]
            : [],
        lensOptions: [],
        selectionRequired: admittedProviderType === 'MIXED',
        selectionInvalid: false,
        selectionSource:
          admittedProviderType === 'SAH' || admittedProviderType === 'RAC'
            ? ('AUTO' as const)
            : ('NONE' as const),
        authorityRevision: 'legacy',
        contextRevision: `legacy:${admittedProviderType}`,
      };
    }
    return {
      tenantId: tenantId ?? null,
      providerType: 'UNKNOWN' as const,
      resolutionStatus: 'UNKNOWN' as const,
      activeProgramFamily: null,
      eligibleProgramFamilies: [],
      lensOptions: [],
      selectionRequired: false,
      selectionInvalid: false,
      selectionSource: 'NONE' as const,
      authorityRevision: 'unseeded',
      contextRevision: 'unseeded:UNKNOWN',
    };
  }, [admittedProviderProgramContext, admittedProviderType, tenantId]);
  const providerProgramContext = useMemo(
    () => constrainProviderProgramContextToTenant(receivedProviderProgramContext, tenantId ?? ''),
    [receivedProviderProgramContext, tenantId],
  );
  const programAwareOrgLabels = useMemo(
    () => ({
      ...resolveOrgLabelsForProgramLens(orgLabels, providerType),
      providerProgramContext,
      activeProgramFamily: providerProgramContext.activeProgramFamily,
      programContextRevision: providerProgramContext.contextRevision,
      programSelectionRequired: providerProgramContext.selectionRequired,
    }),
    [orgLabels, providerProgramContext, providerType],
  );
  const trustedProgramMarker = trustedProgramMarkerFamily(providerProgramContext);
  const programRefreshRequestedForRef = useRef<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    const urlProgram = params.get('program');
    const action = addressBarProgramAction(urlProgram, providerProgramContext);
    if (action === 'none') {
      programRefreshRequestedForRef.current = null;
      return;
    }
    if (action === 'refresh') {
      // An in-app link named another programme. The layout was not re-rendered for it, so this
      // shell still holds the previous one. Do not overwrite the URL: ask the server to resolve
      // the programme from it, once per destination.
      const key = `${pathname}?program=${urlProgram}`;
      if (programRefreshRequestedForRef.current === key) return;
      programRefreshRequestedForRef.current = key;
      router.refresh();
      return;
    }
    if (!providerProgramContext.activeProgramFamily) return;
    params.set('program', providerProgramContext.activeProgramFamily);
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
  }, [pathname, providerProgramContext, router, searchParams]);
  // A programme accepted from the URL is stored nowhere else, so the first request without
  // `?program=` (an in-app link, a save that revalidates, a refresh) would lose it and show the
  // chooser. When the server reports that the lens cookie does not already hold this choice, ask
  // it to remember it. The server re-validates the request; a refusal changes nothing. Setting
  // the cookie makes Next re-render this route once, after which the server reports it stored.
  const rememberRequestedForRef = useRef<string | null>(null);
  useEffect(() => {
    const lens = programLensToRemember(
      providerProgramContext,
      tenantId,
      initialProgramLensRemembered === true,
    );
    const decision = decideRememberAttempt(rememberRequestedForRef.current, lens);
    rememberRequestedForRef.current = decision.lastAttemptedKey;
    if (!lens || !decision.attempt) return;
    const formData = new FormData();
    formData.set('remember', '1');
    formData.set(lens.field, lens.value);
    void setActiveProgramLensAction(formData).catch(() => undefined);
  }, [initialProgramLensRemembered, providerProgramContext, tenantId]);
  const primaryAlias = useMemo(() => primaryAliasForLiveRole(user?.role), [user?.role]);
  const pinBillingPlan = admittedShellContext?.billingPlan ?? null;
  const defaultPins = useMemo(
    () =>
      defaultSidebarPinsForBillingPlan(
        defaultPinsForRoleAndProvider(primaryAlias, providerType),
        pinBillingPlan,
        user?.role,
      ),
    [primaryAlias, providerType, pinBillingPlan, user?.role],
  );
  const legacyDefaultPins = useMemo(() => DEFAULT_PINS_BY_ROLE[primaryAlias], [primaryAlias]);
  const {
    value: pinnedHrefs,
    setValue: setPinnedHrefs,
    isLoaded: pinsLoaded,
  } = useUserPreferences<readonly string[]>('nav-v2:pins', defaultPins);
  const { value: collapsedPreference, setValue: setCollapsed } = useUserPreferences<boolean | null>(
    'nav-v2:collapsed',
    admittedShellContext?.sidebarCollapsed ?? null,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  // Tenant-keyed so a slow prior-tenant badge fetch cannot paint under a new tenant.
  const [navBadgeState, setNavBadgeState] = useState<TenantKeyedNavBadgeState>({
    tenantId: null,
    counts: {},
  });
  // Declared after the existing shell state so earlier hook order is unchanged.
  const [demoPlanModules, setDemoPlanModules] = useState<string[] | null>(
    admittedShellContext?.demoPlanModules ?? null,
  );
  const navBadgeCounts = selectNavBadgeCountsForTenant(navBadgeState, tenantId);
  const flatNavItems = useMemo(() => NAV_V2.flatMap((group) => group.items), []);
  const s8BadgeNavItems = useMemo(
    () =>
      S8_NAV_BADGE_KEYS.map((key) => ({
        key,
        item: flatNavItems.find((navItem) => navItem.badgeKey === key),
      })).filter((entry): entry is { key: NavBadgeKey; item: (typeof flatNavItems)[number] } =>
        Boolean(entry.item),
      ),
    [flatNavItems],
  );
  const visibilityCtx = useMemo(
    () => ({
      liveRole: user?.role,
      payrollRebuildOn: admittedShellContext?.payrollRebuildOn === true,
      billingPlan: admittedShellContext?.billingPlan ?? null,
      providerType,
      providerProgramContext,
      enabledModules,
      tenantId,
      isDemoTenant,
      visibleGoLiveScope,
      tenantHiddenNavigationPaths,
      governmentIntegrations,
    }),
    [
      user?.role,
      admittedShellContext?.billingPlan,
      admittedShellContext?.payrollRebuildOn,
      providerType,
      providerProgramContext,
      enabledModules,
      tenantId,
      isDemoTenant,
      visibleGoLiveScope,
      tenantHiddenNavigationPaths,
      governmentIntegrations,
    ],
  );
  const visibleBadgeKeyStamp = s8BadgeNavItems
    .filter(({ item }) => navV2ItemIsVisible(item, visibilityCtx))
    .map(({ key }) => key)
    .join(',');

  useEffect(() => {
    // Compliance defaults apply only when the preference is unset. Even a saved
    // legacy pack is customisation; migrating it would restore removed shortcuts.
    if (!pinsLoaded || providerType === 'RAC' || isComplianceOnlyPlan(pinBillingPlan)) return;
    if (!arraysEqual(pinnedHrefs, legacyDefaultPins)) return;
    if (arraysEqual(pinnedHrefs, defaultPins)) return;
    // HC pin-set migration must not synchronously update preference state while
    // @dnd-kit sidebar mounts — defer to a transition tick (Phase 1 runtime fix).
    startTransition(() => {
      setPinnedHrefs([...defaultPins]);
    });
  }, [
    defaultPins,
    legacyDefaultPins,
    pinnedHrefs,
    pinsLoaded,
    providerType,
    pinBillingPlan,
    setPinnedHrefs,
  ]);

  const handleTogglePin = useCallback(
    (href: string) => {
      setPinnedHrefs((prev) => {
        const list = [...(prev ?? [])];
        const idx = list.indexOf(href);
        if (idx >= 0) {
          list.splice(idx, 1);
        } else {
          list.push(href);
        }
        return list;
      });
    },
    [setPinnedHrefs],
  );

  const handleReorderPins = useCallback(
    (next: string[]) => {
      setPinnedHrefs(next);
    },
    [setPinnedHrefs],
  );

  // Codex Phase 3 review HIGH finding: the topbar user-menu's Settings
  // link must be gated by the same predicate the sidebar footer uses.
  // /settings is restricted to TENANT_CONFIGURATION_ALLOWED_ROLES per
  // route-access.ts:1168. Compute once per render through the live
  // navV2ItemIsVisible predicate so any new role-cohort change in
  // route-access.ts propagates automatically.
  const canViewSettings = useMemo(() => {
    const settingsItem = FOOTER_NAV_V2.find((item) => item.href === '/settings');
    if (!settingsItem) return false;
    return navV2ItemIsVisible(settingsItem, {
      liveRole: user?.role,
      payrollRebuildOn: admittedShellContext?.payrollRebuildOn === true,
      billingPlan: admittedShellContext?.billingPlan ?? null,
      providerType,
      providerProgramContext,
      enabledModules,
      tenantId,
      isDemoTenant,
      visibleGoLiveScope,
      tenantHiddenNavigationPaths,
      governmentIntegrations,
      frontlineSelfServiceOnly,
    });
  }, [
    user?.role,
    admittedShellContext?.billingPlan,
    providerType,
    providerProgramContext,
    enabledModules,
    tenantId,
    isDemoTenant,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    governmentIntegrations,
    frontlineSelfServiceOnly,
  ]);

  const [peopleBadgeRevision, setPeopleBadgeRevision] = useState(0);
  useEffect(() => {
    if (!tenantId) return;
    const onSaved = (event: Event) => {
      if ((event as CustomEvent<string>).detail === tenantId) {
        setPeopleBadgeRevision((revision) => revision + 1);
      }
    };
    const onStorage = (event: StorageEvent) => {
      // Only removals/clear invalidate. Cache writes must not create refetch loops.
      if (event.newValue === null && isPeopleBadgeStorageKey(event.key, tenantId)) {
        noteExternalPeopleBadgeInvalidation(tenantId);
        setPeopleBadgeRevision((revision) => revision + 1);
      }
    };
    window.addEventListener(PEOPLE_BADGE_INVALIDATED_EVENT, onSaved);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(PEOPLE_BADGE_INVALIDATED_EVENT, onSaved);
      window.removeEventListener('storage', onStorage);
    };
  }, [tenantId]);

  // Sidebar badges (SIRS / MAR / tasks / People cannot-work). Tenant-scoped
  // counts; fail-soft (omit key on error). Visibility gated per-item so a role
  // that cannot see People never pays for the obligation count. Counts are
  // tenant-keyed so a deferred prior-tenant resolve never paints under the
  // active tenant.
  useEffect(() => {
    const effectTenantId = tenantId ?? null;

    // Synchronous clear on tenant change / re-fetch before any async work.
    setNavBadgeState((current) => {
      if (current.tenantId !== effectTenantId) {
        // New tenant (or logout): wipe prior counts immediately under the new key.
        return { tenantId: effectTenantId, counts: {} };
      }
      let changed = false;
      const next = { ...current.counts };
      for (const key of S8_NAV_BADGE_KEYS) {
        if (next[key] !== undefined) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? { tenantId: effectTenantId, counts: next } : current;
    });

    if (!tenantId || !modulesLoaded || s8BadgeNavItems.length === 0) {
      return;
    }

    const visibleKeySet = new Set(visibleBadgeKeyStamp.split(',').filter(Boolean));
    const visibleKeys = S8_NAV_BADGE_KEYS.filter((key) => visibleKeySet.has(key));

    if (visibleKeys.length === 0) {
      return;
    }

    let cancelled = false;
    const timezone = admittedShellContext?.tenantTimezone || DEFAULT_TIMEZONE;

    const cancelDeferredRead = deferNavBadgeRead(() => {
      import('../lib/supabase/client')
        .then(({ createClient }) =>
          cancelled
            ? {}
            : loadNavV2BadgeCounts(createClient(), {
                tenantId,
                authUserId: user?.id ?? null,
                role: user?.role ?? null,
                timezone,
                keys: visibleKeys,
              }),
        )
        .then((counts) => {
          if (cancelled) return;
          setNavBadgeState((current) => {
            // Only merge when state is still keyed to the tenant we fetched for.
            const base = current.tenantId === effectTenantId ? current.counts : {};
            const next = { ...base };
            // Clear S8 keys first so a key that left visibility does not linger.
            for (const key of S8_NAV_BADGE_KEYS) {
              delete next[key];
            }
            return { tenantId: effectTenantId, counts: { ...next, ...counts } };
          });
        })
        .catch((error) => {
          if (cancelled) return;
          console.warn('[dashboard-shell-v2] S8 nav badge counts failed', error);
          setNavBadgeState((current) => {
            if (current.tenantId !== effectTenantId) return current;
            let changed = false;
            const next = { ...current.counts };
            for (const key of S8_NAV_BADGE_KEYS) {
              if (next[key] !== undefined) {
                delete next[key];
                changed = true;
              }
            }
            return changed ? { tenantId: effectTenantId, counts: next } : current;
          });
        });
    });

    return () => {
      cancelled = true;
      cancelDeferredRead();
    };
  }, [
    peopleBadgeRevision,
    admittedShellContext?.tenantTimezone,
    modulesLoaded,
    visibleBadgeKeyStamp,
    tenantId,
    user?.id,
    user?.role,
  ]);

  const customerLegalGateRequired =
    !!user && user.role !== 'CLIENT' && user.role !== 'FAMILY_CARER';
  if (shellConfigError) {
    return (
      <main className="min-w-0">
        <PageContainer>
          <DashboardPageHeader
            title="Workspace settings unavailable"
            description="Your organisation settings could not be verified."
          />
          <StateKit
            state="failed"
            description="Try again to load this workspace."
            onRetry={() => {
              setModulesLoaded(false);
              setShellConfigError(false);
              setShellConfigAttempt((attempt) => attempt + 1);
            }}
          />
        </PageContainer>
      </main>
    );
  }
  if (resolvedShellTenantId.current !== tenantId) {
    return (
      <AppLoadingState
        title="Opening workspace"
        description="Loading your organisation settings."
      />
    );
  }
  if (!modulesLoaded || (customerLegalGateRequired && !customerLegalLoaded)) {
    return (
      <AppLoadingState
        title="Opening workspace"
        description="Loading organisation settings, module access and agreement status."
      />
    );
  }
  const providerRuntimeEvidenceRevision = providerRuntimeEvidenceEnabledForSupabaseUrl(
    process.env.NEXT_PUBLIC_SUPABASE_URL,
  )
    ? PROVIDER_RUNTIME_EVIDENCE_REVISION
    : undefined;

  return (
    <>
      <div
        data-experience-brand={experienceBrandEnabled ? 'true' : undefined}
        data-testid="experience-program-binding-marker"
        data-program-family={trustedProgramMarker}
        data-program-context-revision={providerProgramContext.contextRevision}
        data-program-authority-revision={providerProgramContext.authorityRevision}
        data-experience-evidence-revision={providerRuntimeEvidenceRevision}
        data-program-binding-state={
          trustedProgramMarker === 'SHARED'
            ? 'SELECTION_REQUIRED'
            : providerProgramContext.resolutionStatus
        }
        className={
          isRosterDispatchFullWorkspace
            ? 'min-h-screen bg-fog'
            : 'flex h-svh min-h-0 overflow-hidden bg-surface-canvas'
        }
      >
        {/* Preferences enhance the navigation; their loading never hides it. */}
        {!isRosterDispatchFullWorkspace && (
          <ResponsiveSidebar
            preference={collapsedPreference}
            rail={
              <NavV2SidebarCollapsed
                payrollRebuildOn={admittedShellContext?.payrollRebuildOn === true}
                currentPath={pathname}
                liveRole={user?.role}
                providerType={providerType}
                programContext={providerProgramContext}
                enabledModules={enabledModules}
                billingPlan={admittedShellContext?.billingPlan ?? null}
                tenantId={tenantId}
                isDemoTenant={isDemoTenant}
                visibleGoLiveScope={visibleGoLiveScope}
                tenantHiddenNavigationPaths={tenantHiddenNavigationPaths}
                frontlineSelfServiceOnly={frontlineSelfServiceOnly}
                pinnedHrefs={pinnedHrefs}
                badgeCounts={navBadgeCounts}
                onCmdK={() => triggerCmdK('sidebar-collapsed')}
                onExpand={() => {
                  // Phase 6 telemetry: lock the toggle direction so we
                  // can distinguish "collapsed→expanded" from the inverse.
                  trackNavV2Event('nav_v2_sidebar_collapsed', {
                    source: 'sidebar-collapsed',
                    collapsed: false,
                  });
                  setCollapsed(false);
                }}
                onHelpChat={openHelpChat}
              />
            }
            expanded={
              <NavV2Sidebar
                payrollRebuildOn={admittedShellContext?.payrollRebuildOn === true}
                currentPath={pathname}
                liveRole={user?.role}
                providerType={providerType}
                programContext={providerProgramContext}
                enabledModules={enabledModules}
                billingPlan={admittedShellContext?.billingPlan ?? null}
                tenantId={tenantId}
                isDemoTenant={isDemoTenant}
                visibleGoLiveScope={visibleGoLiveScope}
                tenantHiddenNavigationPaths={tenantHiddenNavigationPaths}
                frontlineSelfServiceOnly={frontlineSelfServiceOnly}
                pinnedHrefs={pinnedHrefs}
                badgeCounts={navBadgeCounts}
                onReorderPins={handleReorderPins}
                onTogglePin={handleTogglePin}
                onCmdK={() => triggerCmdK('sidebar')}
                onCollapse={() => {
                  trackNavV2Event('nav_v2_sidebar_collapsed', {
                    source: 'sidebar',
                    collapsed: true,
                  });
                  setCollapsed(true);
                }}
                onHelpChat={openHelpChat}
              />
            }
          />
        )}

        {/* Mobile drawer (retires ADR-0036 §M1) */}
        {!isRosterDispatchFullWorkspace && (
          <NavV2MobileDrawer
            payrollRebuildOn={admittedShellContext?.payrollRebuildOn === true}
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            currentPath={pathname}
            liveRole={user?.role}
            providerType={providerType}
            programContext={providerProgramContext}
            enabledModules={enabledModules}
            billingPlan={admittedShellContext?.billingPlan ?? null}
            tenantId={tenantId}
            isDemoTenant={isDemoTenant}
            visibleGoLiveScope={visibleGoLiveScope}
            tenantHiddenNavigationPaths={tenantHiddenNavigationPaths}
            frontlineSelfServiceOnly={frontlineSelfServiceOnly}
            pinnedHrefs={pinnedHrefs}
            badgeCounts={navBadgeCounts}
            onReorderPins={handleReorderPins}
            onTogglePin={handleTogglePin}
            onCmdK={() => triggerCmdK('mobile-drawer')}
            onHelpChat={openHelpChat}
          />
        )}

        {/* Main column */}
        <div
          className={
            isRosterDispatchFullWorkspace
              ? 'min-h-screen min-w-0'
              : 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
          }
        >
          {!isRosterDispatchFullWorkspace && (
            <header
              className="ds-shell-navigation z-30 flex h-14 shrink-0 min-w-0 items-center gap-2 max-[360px]:h-auto max-[360px]:flex-wrap max-[360px]:py-2 border-b border-border bg-white/90 px-2 backdrop-blur sm:px-5"
              data-nav-v2-topbar="true"
            >
              {/* Mobile hamburger — opens drawer below lg breakpoint */}
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-label="Open navigation menu"
                className={`${controlSizeClassName('toolbar')} inline-flex w-9 shrink-0 items-center justify-center rounded-control border border-control-border/30 !p-0 text-text-muted hover:bg-surface-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-focus lg:hidden`}
                data-nav-v2-hamburger="true"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
              <div className="min-w-0 flex-1 max-[360px]:order-last max-[360px]:basis-full sm:max-w-[22rem] sm:flex-[0_1_22rem]">
                <FacilitySwitcher placement="header" includeServiceAreas={!isComplianceTrial} />
              </div>
              <div className="hidden flex-1 max-[360px]:block sm:block" />
              <div className="hidden shrink-0 border-l border-border pl-2 sm:block">
                <PageHelpOpener path={pathname} />
              </div>
              <div className="shrink-0">
                <NotificationBell
                  programFamily={trustedProgramMarker}
                  programContextRevision={providerProgramContext.contextRevision}
                />
              </div>
              <div className="shrink-0">
                <TopbarUserMenu
                  onHelp={() => dispatchOpenHelpChat(pathname)}
                  displayName={displayName}
                  initials={initials}
                  roleName={roleName}
                  canViewSettings={canViewSettings}
                  canViewPlatformConsole={user?.role === 'SYSTEM_ADMIN'}
                />
              </div>
            </header>
          )}

          {/* Banners + gated children. `id="main-content"` is the
              SkipToContent target — WCAG 2.4.1. `tabIndex={-1}` makes
              the region programmatically focusable so the skip-link's
              fragment-navigation actually moves focus on Enter
              (Sprint 17 codex pass 1 P2). */}
          <main
            id="main-content"
            tabIndex={-1}
            className={
              isRosterDispatchFullWorkspace
                ? 'min-h-screen focus:outline-none'
                : 'min-h-0 flex-1 overflow-x-hidden overflow-y-auto focus:outline-none'
            }
          >
            <div data-print-chrome="true">
              {!isRosterDispatchFullWorkspace && (
                <DemoEnvironmentBanner initialIsDemo={isDemoTenant} />
              )}
              {/* Platform announcements — re-wired after S22 deleted legacy dashboard-shell.tsx */}
              {!isCiFree && <SystemAnnouncementBanner />}
              {!isRosterDispatchFullWorkspace && !isCiFree && (
                <MfaEnforcementBanner
                  suppress={isDemoTenant}
                  initialState={admittedShellContext?.mfaReminder}
                />
              )}
              {!isCiFree &&
                !isRosterDispatchFullWorkspace &&
                !suppressOperationalShellBanners(pathname) &&
                showOnboarding &&
                pathname !== '/onboarding' &&
                isRoleAllowedForPath('/onboarding', user?.role) && (
                  <TenantSetupBanner
                    onDismiss={() => setShowOnboarding(false)}
                    resolvedSteps={onboardingStepsCompleted}
                    deferredSteps={onboardingStepsDeferred}
                    blockedSteps={onboardingStepsBlocked}
                    totalSteps={onboardingStepsTotal}
                  />
                )}
              {!isCiFree &&
                !isRosterDispatchFullWorkspace &&
                !suppressOperationalShellBanners(pathname) &&
                bannerData && (
                  <SubscriptionBanner
                    status={bannerData.subscriptionStatus}
                    trialEndsAt={bannerData.trialEndsAt}
                    declaredBeds={bannerData.declaredBeds}
                    declaredClients={bannerData.declaredClients}
                    actualBeds={bannerData.actualBeds}
                    actualClients={bannerData.actualClients}
                    isComplianceTrial={isComplianceTrial}
                    canManageBilling={isRoleAllowedForPath('/settings/billing', user?.role)}
                  />
                )}
            </div>
            <TenantTimezoneProvider value={admittedShellContext?.tenantTimezone ?? null}>
              <OrgLabelsProvider value={programAwareOrgLabels}>
                <ModuleProvider
                  value={{
                    billingPlan: admittedShellContext?.billingPlan ?? null,
                    enabledModules,
                    tenantId,
                    isDemoTenant,
                    demoPlanModules,
                    isCiFree,
                    modulesLoaded,
                    isSubscriptionBlocked: isSubBlocked,
                    moduleLoadError,
                    visibleGoLiveScope,
                    tenantHiddenNavigationPaths,
                    frontlineSelfServiceOnly,
                    shiftSwapsEnabled,
                    payrollRebuildOn: admittedShellContext?.payrollRebuildOn === true,
                  }}
                >
                  {moduleLoadError && (
                    <div className="mx-4 mt-2 flex items-center justify-between rounded-card border border-status-danger-border bg-status-danger-bg px-4 py-3">
                      <p className="text-body text-status-danger-fg">
                        Unable to load module configuration. Some features may be unavailable.
                      </p>
                      <button
                        onClick={() => window.location.reload()}
                        className="ml-4 rounded-control bg-red-600 px-3 py-1.5 text-body font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  <ModuleGate>
                    {customerLegalGateRequired && customerLegalError ? (
                      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-3xl items-center justify-center px-4 py-8">
                        <Card className="w-full max-w-2xl">
                          <CardContent className="space-y-4 pt-6">
                            <div>
                              <h2 className="font-heading text-section-title text-statura-ink">
                                Customer agreement status unavailable
                              </h2>
                              <p className="mt-2 text-body text-text-muted">
                                Statura could not confirm whether the current customer legal pack
                                has been accepted for this tenant. Retry before continuing.
                              </p>
                              <p className="mt-2 text-body text-status-danger-fg">
                                {customerLegalError}
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                void refreshCustomerLegalState();
                              }}
                              className="inline-flex items-center rounded-control bg-statura-green-accessible px-4 py-2 text-body font-medium text-white transition hover:bg-statura-green-accessible-hover"
                            >
                              Retry
                            </button>
                          </CardContent>
                        </Card>
                      </div>
                    ) : user?.role === 'TENANT_ADMIN' && customerLegalState?.blocking ? (
                      <CustomerLegalGate
                        state={customerLegalState}
                        onAccepted={(nextState) => {
                          setCustomerLegalState(nextState);
                          setCustomerLegalError(null);
                          setCustomerLegalLoaded(true);
                        }}
                      />
                    ) : customerLegalGateRequired && customerLegalState?.blocking ? (
                      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-3xl items-center justify-center px-4 py-8">
                        <Card className="w-full max-w-2xl">
                          <CardContent className="space-y-4 pt-6">
                            <h2 className="font-heading text-section-title text-statura-ink">
                              Customer agreement required
                            </h2>
                            <p className="text-body text-text-muted">
                              Your organisation has not yet accepted the current Statura customer
                              legal pack (Master Services Agreement, Data Processing Schedule,
                              Subprocessor Schedule, and Authorised Signatory attestation). Please
                              ask an organisation administrator to sign in and complete this step
                              before continuing.
                            </p>
                            <p className="text-body text-text-muted">
                              Until acceptance is recorded, the platform cannot accept regulated
                              mutations or billing changes for this tenant.
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      <RoleGate>
                        <PageContainer
                          key={`${tenantId ?? 'none'}:${user?.role ?? 'unknown'}:${providerProgramContext.contextRevision}`}
                          data-visual-density="productive"
                        >
                          <SectionTabsProvider>
                            <NavV2SectionTabs pathname={pathname} />
                            {children}
                          </SectionTabsProvider>
                        </PageContainer>
                      </RoleGate>
                    )}
                  </ModuleGate>
                  <DeferredCommandPalette
                    key={`${tenantId ?? 'none'}:${user?.role ?? 'unknown'}:${providerProgramContext.contextRevision}`}
                  />
                </ModuleProvider>
              </OrgLabelsProvider>
            </TenantTimezoneProvider>
          </main>
        </div>
      </div>
      <div data-print-chrome="true">
        {isRosterDispatchFullWorkspace ? (
          <div className="fixed right-3 top-3 z-[120]">
            <PageHelpOpener
              path={pathname}
              className="inline-flex h-9 w-9 items-center justify-center rounded-control border border-border bg-surface-sheet text-text-muted hover:bg-surface-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            />
          </div>
        ) : null}
        <HelpChatWidget
          key={`${tenantId ?? 'none'}:${user?.role ?? 'unknown'}:${providerProgramContext.contextRevision}`}
          userName={displayName}
          activeProgramFamily={providerProgramContext.activeProgramFamily}
          providerType={providerType}
          programContextRevision={providerProgramContext.contextRevision}
          assistantConversationEnabled={initialAssistantConversationEnabled === true}
          assistantProactiveEnabled={initialAssistantProactiveEnabled === true}
        />
        {!isRosterDispatchFullWorkspace ? <KeyboardShortcutsHint /> : null}
      </div>
    </>
  );
}

export function DashboardShellV2({
  children,
  initialOrgType,
  initialProviderType,
  initialProviderProgramContext,
  experienceBrandEnabled,
  initialShellContext,
  initialAuthUser,
  initialProgramLensRemembered,
  initialAssistantConversationEnabled,
  initialAssistantProactiveEnabled,
}: {
  children: React.ReactNode;
  initialOrgType?: string | null;
  initialProviderType?: ProviderType;
  initialProviderProgramContext?: ProviderProgramContext;
  experienceBrandEnabled?: boolean;
  initialShellContext?: InitialShellContext;
  initialAuthUser?: AuthUserProfile | null;
  initialAssistantConversationEnabled?: boolean;
  initialAssistantProactiveEnabled?: boolean;
  /** Server fact: the lens cookie already holds the programme this render resolved. */
  initialProgramLensRemembered?: boolean;
}) {
  return (
    <AuthGuard initialUser={initialAuthUser}>
      <SessionSecurityProvider>
        <DashboardShellV2Inner
          initialOrgType={initialOrgType}
          initialProviderType={initialProviderType}
          initialProviderProgramContext={initialProviderProgramContext}
          experienceBrandEnabled={experienceBrandEnabled}
          initialShellContext={initialShellContext}
          initialAuthUser={initialAuthUser}
          initialProgramLensRemembered={initialProgramLensRemembered}
          initialAssistantConversationEnabled={initialAssistantConversationEnabled}
          initialAssistantProactiveEnabled={initialAssistantProactiveEnabled}
        >
          {children}
        </DashboardShellV2Inner>
      </SessionSecurityProvider>
    </AuthGuard>
  );
}
